// JOBE-0044.4: LibraryTable — middle-column result table. Header row,
// group dividers, body rows, "Load more" footer. The skeleton / empty /
// error variants here are basic placeholders; JOBE-0044.14 refines.

import { useMemo } from 'react';
import { GroupDivider } from './GroupDivider.jsx';
import { LibraryRow } from './LibraryRow.jsx';
import { pickRow } from '../entitybrowser/rowRegistry.js';
import { groupLabel } from './groupLabel.js';

export function LibraryTable({
  rows,
  loadState,
  error,
  nextCursor,
  onLoadMore,
  selectedUrn,
  onSelect,
  onChipAppend,
  onRetry,
}) {
  // Compute group transitions. The label flips whenever
  // `groupLabel(rows[i].lastChangedAt, now)` differs from the prior row.
  const items = useMemo(() => {
    const now = Date.now();
    const out = [];
    let prev = null;
    for (const row of rows) {
      const label = groupLabel(row.lastChangedAt, now);
      if (label !== prev) {
        out.push({ kind: 'divider', label, key: `div:${label}:${row.urn}` });
        prev = label;
      }
      out.push({ kind: 'row', row, key: row.urn });
    }
    return out;
  }, [rows]);

  const isInitialLoading = loadState === 'loading' && rows.length === 0;
  const isError = loadState === 'error' && rows.length === 0;
  const isEmpty = loadState === 'ready' && rows.length === 0;

  return (
    <section className="library-table" aria-label="Library results">
      <header className="library-table-header" role="row">
        <span className="library-row-star" aria-hidden="true" />
        <span className="library-table-col library-table-col-title">Title</span>
        <span className="library-table-col library-table-col-kind">Kind</span>
        <span className="library-table-col library-table-col-owner">Owner</span>
        <span className="library-table-col library-table-col-where">Where</span>
        <span className="library-table-col library-table-col-updated">Updated</span>
      </header>

      {isInitialLoading && <SkeletonRows />}
      {isError && <ErrorPlaceholder error={error} onRetry={onRetry} />}
      {isEmpty && <EmptyPlaceholder />}

      {rows.length > 0 && (
        <ul className="library-table-body">
          {items.map((it) => {
            if (it.kind === 'divider') {
              return <GroupDivider key={it.key} label={it.label} />;
            }
            const KindRow = pickRow(it.row);
            if (KindRow) {
              return (
                <KindRow
                  key={it.key}
                  row={it.row}
                  selected={it.row.urn === selectedUrn}
                  onSelect={onSelect}
                  onChipAppend={onChipAppend}
                />
              );
            }
            return (
              <LibraryRow
                key={it.key}
                row={it.row}
                selected={it.row.urn === selectedUrn}
                onSelect={onSelect}
                onChipAppend={onChipAppend}
              />
            );
          })}
        </ul>
      )}

      {nextCursor && rows.length > 0 && (
        <div className="library-table-footer">
          <button
            type="button"
            className="library-table-load-more"
            onClick={onLoadMore}
            disabled={loadState === 'loading'}
          >
            {loadState === 'loading' ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </section>
  );
}

// SkeletonRows: basic placeholder. JOBE-0044.14 will add the proper
// skeleton / cold-start variants.
function SkeletonRows() {
  return (
    <ul className="library-table-body library-table-skeleton" aria-busy="true">
      {[0, 1, 2, 3].map((i) => (
        <li key={i} className="library-row library-row-skeleton">
          <span className="skel-line skel-w-80" />
        </li>
      ))}
    </ul>
  );
}

// EmptyPlaceholder: basic placeholder. JOBE-0044.14 will add proper
// filter-empty variants.
function EmptyPlaceholder() {
  return (
    <div className="library-table-empty">
      <p className="muted">No results.</p>
    </div>
  );
}

// ErrorPlaceholder: basic placeholder. JOBE-0044.14 will add proper
// hard-error / Cedar-denied variants.
function ErrorPlaceholder({ error, onRetry }) {
  const status = error?.status || 0;
  let msg = "Couldn't load the library.";
  if (status === 400) msg = 'That filter set is invalid.';
  if (status === 401) msg = 'Please sign in to view the library.';
  if (status === 503) msg = 'The graph backend is unavailable right now.';
  return (
    <div className="library-table-empty">
      <p>{msg}</p>
      {onRetry && (
        <button type="button" className="library-table-retry" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}
